import type { Hlc, Id } from '../types';

// Encje synchronizowane polami (last-writer-wins per pole) i ich pola edytowalne.
// Nazwy pól = nazwy właściwości w TS (camelCase) po obu stronach.
export const LWW_FIELDS = {
  chains: ['name', 'mergedInto', 'deleted'],
  stores: ['name', 'chainId', 'address', 'favorite', 'mergedInto', 'deleted', 'lat', 'lng', 'geoSource', 'externalRef'],
  lists: ['title', 'storeId', 'status', 'createdMs', 'finishedMs', 'deleted'],
  listItems: [
    'listId',
    'rawText',
    'name',
    'productId',
    'qty',
    'unit',
    'amountValue',
    'amountUnit',
    'position',
    'status',
    'note',
    'partialQty',
    'substitute',
    'checkedMs',
    'checkedBy',
    'addedBy',
    'addedMs',
    'suggestion',
    'suggestedBy',
    'suggestedMs',
    'articleId',
    'deleted',
  ],
  products: ['name', 'normKey', 'mergedInto', 'deleted'],
  productLinks: ['normKey', 'productId', 'verdict', 'deleted'],
  visits: ['storeId', 'listId', 'deviceId', 'userId', 'startedMs', 'finishedMs', 'deleted', 'lat', 'lng', 'accM'],
  // Paragony (zapisuje je serwer z łącznika Lidl Plus); kwoty w groszach.
  articles: [
    'source',
    'code',
    'rawName',
    'name',
    'productId',
    'linkSource',
    'weight',
    'lastShelfGr',
    'lastPaidGr',
    'lastBoughtMs',
    'deleted',
  ],
  receipts: [
    'source',
    'externalId',
    'storeCode',
    'storeName',
    'storeId',
    'purchasedMs',
    'totalGr',
    'depositGr',
    'savingsGr',
    'linesCount',
    'listId',
    'listState',
    'deleted',
  ],
  receiptLines: [
    'receiptId',
    'idx',
    'articleId',
    'rawName',
    'qty',
    'weight',
    'unitGr',
    'valueGr',
    'discountGr',
    'discounts',
    'depositGr',
    'listItemId',
    'match',
    'deleted',
  ],
  // Promocje na artykuły domu (gazetka, lidl.pl, oferty sklepu, kupony Lidl Plus); zapisuje je serwer.
  promotions: [
    'source',
    'code',
    'articleId',
    'title',
    'discount',
    'priceGr',
    'oldPriceGr',
    'startMs',
    'endMs',
    'lidlPlus',
    'couponId',
    'activated',
    'deleted',
  ],
} as const satisfies Record<string, readonly string[]>;

export type LwwEntity = keyof typeof LWW_FIELDS;
export type AppendEntity = 'orderEvents';
export type SyncEntity = LwwEntity | AppendEntity;

export const LWW_ENTITIES = Object.keys(LWW_FIELDS) as LwwEntity[];

export function isLwwEntity(value: unknown): value is LwwEntity {
  return typeof value === 'string' && Object.hasOwn(LWW_FIELDS, value);
}

export function isLwwField(entity: LwwEntity, field: string): boolean {
  return (LWW_FIELDS[entity] as readonly string[]).includes(field);
}

export type FieldHlc = Record<string, Hlc>;

/** Wiersz encji LWW w postaci przesyłanej między klientem a serwerem. */
export interface LwwRow {
  id: Id;
  fieldHlc: FieldHlc;
  [field: string]: unknown;
}

/** Zdarzenie dziennika kolejności w postaci przesyłanej (szczegóły typu w order/). */
export interface EventRow {
  id: Id;
  storeId: Id;
  hlc: Hlc;
  kind: string;
  [field: string]: unknown;
}

/** Pola z identyfikatorem użytkownika — serwer przyjmuje tylko null albo zalogowanego użytkownika. */
export const USER_REF_FIELDS = ['checkedBy', 'addedBy', 'suggestedBy', 'userId'] as const;

export type ListStatus = 'active' | 'done' | 'archived';
export type LinkVerdict = 'same' | 'different';

/** Skąd artykuł ma produkt: dopasowanie paragonu do listy, AI, decyzja użytkownika, przypięcie przy pisaniu listy; 'none' = sprawdzone, bez produktu. */
export type ArticleLinkSource = 'match' | 'ai' | 'user' | 'pin' | 'none';
/** Paragon ↔ lista: dopasowana sama, kilka równie dobrych (pytanie), żadna, wybrana ręcznie. */
export type ReceiptListState = 'matched' | 'ambiguous' | 'none' | 'manual';
/**
 * Linia paragonu ↔ pozycja listy: auto (kod, produkt, nazwa), ai (pewne od modelu), suggested (do potwierdzenia),
 * confirmed (potwierdzone albo przypisane ręcznie), rejected (odrzucona propozycja — linia spoza listy).
 */
export type LineMatch = 'auto' | 'ai' | 'suggested' | 'confirmed' | 'rejected';
/** Skąd promocja: gazetka (AI), lidl.pl, oferty sklepu w aplikacji, kupon Lidl Plus. */
export type PromoSource = 'leaflet' | 'web' | 'offers' | 'coupon';
